// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';

/**
 * Advisor access to a relationship's actionable Suggested Steps (Panel 2 of the
 * Overview). Proxied by advisor-bff to the resource server at
 * /api/profiles/{profileId}/suggestions. See
 * docs/specs/relationship-summary-panels-prd.md.
 */

export type SuggestionStatus = 'SUGGESTED' | 'EXECUTED' | 'DISMISSED' | 'EXPIRED';

export interface RelationshipSuggestion {
  id: string;
  type: string;
  badgeLabel?: string | null;
  title: string;
  body?: string | null;
  /** EXECUTE_CASHFLOW_MODEL | OPEN_LOA_FLOW | LINK | NONE */
  actionKind: string;
  /** Whether the Execute button is offered (actionKind != NONE). */
  executable: boolean;
  status: SuggestionStatus;
  executedRef?: string | null;
  reviewNote?: string | null;
  sortOrder: number;
}

export interface SuggestionActionResult {
  id: string;
  status: SuggestionStatus;
  executedRef?: string | null;
  /** Advisor-UI route to navigate to after executing (e.g. the new scenario's cashflow view). */
  deepLink?: string | null;
  /** True when some actions could not be fully applied. */
  partial: boolean;
  notes: string[];
}

export const suggestionsApi = {
  /** Active (SUGGESTED) suggestions for the relationship. */
  async list(profileId: string): Promise<RelationshipSuggestion[]> {
    const { data } = await api.get<RelationshipSuggestion[]>(`/profiles/${profileId}/suggestions`);
    return data;
  },

  /** Execute a suggested step (e.g. create the what-if cashflow model). */
  async execute(profileId: string, suggestionId: string): Promise<SuggestionActionResult> {
    const { data } = await api.post<SuggestionActionResult>(
      `/profiles/${profileId}/suggestions/${suggestionId}/execute`,
    );
    return data;
  },

  /** Dismiss a suggested step. */
  async dismiss(profileId: string, suggestionId: string, reason?: string): Promise<SuggestionActionResult> {
    const { data } = await api.post<SuggestionActionResult>(
      `/profiles/${profileId}/suggestions/${suggestionId}/dismiss`,
      { reason },
    );
    return data;
  },
};
